import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

import { TypeUtils } from '../../framework/utils/UtilityTypes';

/**
 * フォーマット文字列
 */
const formatStrings = {
  DATETIME: 'yyyy/MM/dd HH:mm',
  DATETIME_API: "yyyy-MM-dd'T'HH:mm:ss",
  DATETIME_OF_WEEK: 'MM/dd (ccccc) HH:mm',
  DATE: 'yyyy/MM/dd',
  DATE_API: 'yyyy-MM-dd',
  DATE_OF_WEEK: 'MM/dd (ccccc)',
} as const;

const timeFormatter = (hhmm: string | null | undefined, emptyValue = '') => {
  const regexp = new RegExp(/^([01][0-9]|2[0-3])[0-5][0-9]$/);
  return TypeUtils.isNil(hhmm) || !regexp.test(hhmm)
    ? emptyValue
    : `${hhmm.substring(0, 2)}:${hhmm.substring(2, 4)}`;
};

const workingTimeFormatter = (
  workingTime: number | null | undefined,
  emptyValue = ''
) => {
  if (TypeUtils.isNil(workingTime)) {
    return emptyValue;
  } else {
    const hours = (workingTime / 60) | 0; // 切り捨て
    const minutes = workingTime - hours * 60;
    return `${hours}時間${minutes}分`;
  }
};

/**
 *
 * @param date -
 * @returns
 */
const toHyphen = (date: string) => {
  if (/^\d{4}\/\d{2}\/\d{2}T/g.test(date)) {
    return date.replace(/^(\d{4})\/(\d{2})\/(\d{2})T/, '$1-$2-$3 ');
  }
  if (/^\d{4}\/\d{2}\/\d{2}/g.test(date)) {
    return date.replace(/^(\d{4})\/(\d{2})\/(\d{2})/, '$1-$2-$3');
  }
  return date;
};

/**
 *
 * @param date -
 * @returns
 */
const toSlash = (date: string) => {
  if (/^\d{4}-\d{2}-\d{2}T/g.test(date)) {
    return date.replace(/^(\d{4})-(\d{2})-(\d{2})T/, '$1/$2/$3 ');
  }
  if (/^\d{4}-\d{2}-\d{2}/g.test(date)) {
    return date.replace(/^(\d{4})-(\d{2})-(\d{2})/, '$1/$2/$3');
  }
  return date;
};

/**
 *
 * @param date -
 * @returns
 */
const createDate = (date: Date | string | number) =>
  new Date(typeof date === 'string' ? toSlash(date) : date);

/**
 *
 * @param datetime - 日時文字列
 * @param toApi - リクエストパラメータのための変換ならば true
 * @returns
 */
const dateTimeFormatter = (datetime: Date, toApi?: boolean) =>
  format(datetime, toApi ? formatStrings.DATETIME_API : formatStrings.DATETIME);

/**
 *
 * @param datetime - 日時文字列
 * @param toApi - リクエストパラメータのための変換ならば true
 * @returns
 */
const dateTimeStringFormatter = (
  datetime?: Date | string | null,
  toApi?: boolean,
  emptyValue = ''
) =>
  TypeUtils.isNilString(datetime)
    ? toApi
      ? undefined
      : emptyValue
    : dateTimeFormatter(createDate(datetime), toApi);

/**
 * 曜日あり
 *
 * @param datetime - 日時文字列
 * @returns
 */
const dateTimeOfWeekFormatter = (datetime: Date) =>
  format(datetime, formatStrings.DATETIME_OF_WEEK, { locale: ja });

/**
 * 曜日あり
 *
 * @param datetime - 日時文字列
 * @returns
 */
const dateTimeOfWeekStringFormatter = (
  datetime?: Date | string | null,
  emptyValue = ''
) =>
  TypeUtils.isNilString(datetime)
    ? emptyValue
    : dateTimeOfWeekFormatter(createDate(datetime));

/**
 *
 * @param date - 日付文字列
 * @returns
 */
const dateFormatter = (
  date?: Date | string | null,
  toApi?: boolean,
  emptyValue = ''
) =>
  TypeUtils.isNilString(date)
    ? toApi
      ? undefined
      : emptyValue
    : format(
        createDate(date),
        toApi ? formatStrings.DATE_API : formatStrings.DATE
      );

/**
 * 曜日あり
 *
 * @param date - 日付
 * @returns
 */
const dateOfWeekFormatter = (date: Date) =>
  format(date, formatStrings.DATE_OF_WEEK, { locale: ja });

/**
 * 曜日あり
 *
 * @param date - 日付文字列
 * @returns
 */
const dateOfWeekStringFormatter = (
  date?: Date | string | null,
  emptyValue = ''
) =>
  TypeUtils.isNilString(date)
    ? emptyValue
    : dateOfWeekFormatter(createDate(date));

export const Formatter = {
  format: formatStrings,
  time: timeFormatter,
  workingTime: workingTimeFormatter,
  createDate,
  date: dateFormatter,
  dateTime: dateTimeFormatter,
  dateTimeString: dateTimeStringFormatter,
  dateTimeOfWeek: dateTimeOfWeekFormatter,
  dateTimeOfWeekString: dateTimeOfWeekStringFormatter,
  dateOfWeek: dateOfWeekFormatter,
  dateOfWeekString: dateOfWeekStringFormatter,
  toHyphen,
  toSlash,
};
